import {
  IDate,
  AgentTypeEnum,
  AccountTypeEnum,
  InvoiceOperationEnum,
  AgencyInvoiceStatusEnum,
  ProductTypeEnum,
  IDateRange,
  OperationEnum,
} from './types';
import {
  sleep,
  randomString,
  isSameMonth,
  formatDate,
  stringFromIDate,
} from '@/utils/index';
import request from '@/utils/request';
import {
  get_booking_detail,
  mockBookingDetailsCache,
  WithInternalStatus,
} from './booking';
import { get_agency_sales_report_detail } from './salesreport';
import { mockAgentSummariesCache, mockAgentsCache } from './agent';
import {
  agencyInvoiceStatusToInvoiceStatus,
  bookingStatusToInvoiceStatus,
} from '@/utils/invoice';
import { mockProductSummariesCache } from './product';

// data type
export namespace search_invoices {
  export interface Request {
    invoiceTypes?: InvoiceTypeEnum[];
    generatedDateRange?: IDateRange;
    targetYear?: number;
    priceRange?: Request.PriceRange;
    agentIds: string[];
    targetProductIds?: string[];
    paymentStatuses?: PaymentStatusEnum[];
    paidDateRange?: IDateRange;
    accountingSlipStatuses?: AccountingSlipStatusEnum[];
    statuses?: InvoiceStatusEnum[];
  }

  export namespace Request {
    export interface PriceRange {
      currency?: string;
      from?: number;
      to?: number;
    }
  }

  export interface OrderInfo {
    productId: string;
    productName: string;
    soldAmount: number;
    refundAmount: number;
    type: ProductTypeEnum;
  }

  export interface InvoiceSummary {
    id: string;
    agentId: string;
    agentName: string;
    agentType: AgentTypeEnum;
    invoiceType: InvoiceTypeEnum;
    status: InvoiceStatusEnum;
    generatedAt: string;
    billingAmount: number;
    paidAmount: number;
    paidAt: IDate | null;
    orders: OrderInfo[];
    isDelayed: boolean;
    accountingSlipFinished?: boolean;
    notes: string[];
    extraInfo: InvoiceSummary.ExtraInfo;
  }

  export namespace InvoiceSummary {
    export interface ExtraInfo {
      [key: string]: string;
    }
    export interface ExtraInfoEntry {
      key: string;
      value: string;
    }
  }

  export interface Response {
    invoices: InvoiceSummary[];
    csvBlob: string;
  }

  export enum InvoiceTypeEnum {
    UNKNOWN_TYPE = 'UNKNOWN_TYPE',
    BOOKING_INVOICE = 'BOOKING_INVOICE',
    AGENTCY_INVOICE = 'AGENTCY_INVOICE',
  }

  export enum PaymentStatusEnum {
    UNKNOWN_PAYMENT_STATUS = 'UNKNOWN_PAYMENT_STATUS',
    PAID = 'PAID',
    NOT_PAID = 'NOT_PAID',
  }

  export enum AccountingSlipStatusEnum {
    UNKNOWN_ACCOUNTING_SLIP_STATUS = 'UNKNOWN_ACCOUNTING_SLIP_STATUS',
    FINISHED = 'FINISHED',
    NOT_FINISHED = 'NOT_FINISHED',
  }

  export enum InvoiceStatusEnum {
    UNKNOWN_STATUS = 'UNKNOWN_STATUS',
    GENERATED = 'GENERATED',
    CONFIRMED_INIT = 'CONFIRMED_INIT',
    CONFIRMED_SUP = 'CONFIRMED_SUP',
    CONFIRMED = 'CONFIRMED',
    PAYMENT_CONFIRMED = 'PAYMENT_CONFIRMED',
  }
}

export namespace get_agency_invoice_detail {
  export interface Request {
    invoiceId: string;
  }

  export interface Product {
    productId: string;
    productName: string;
    amount: number;
    currency: string;
    totalNetPrice: number;
    totalGrossPrice: number;
  }

  export interface AgencySalesReport {
    id: string;
    products: Product[];
    currency: string;
    totalNetPrice: number;
    totalGrossPrice: number;
    reportedAt: string;
  }

  export interface AgencySalesReports {
    reports: AgencySalesReport[];
    grandTotalNetPrice: number;
    grandTotalGrossPrice: number;
  }

  export interface Destination {
    address: string;
    recipient: string;
    contact: string;
  }

  export interface BankTransferDetail {
    actualAmount: number;
    date: IDate;
    bankNumber: string;
    bankName: string;
    branchNumber: string;
    branchName: string;
    accountNumber: string;
    accountName: string;
    businessCode: string;
    accountCode: string;
    auxiliaryCode: string;
  }

  export interface InvoiceDocument {
    productId: string;
    productName: string;
    url: string;
  }

  export interface Discrepancy {
    title: string;
    currency: string;
    amount: number;
  }

  export interface OperationLog {
    accountType: AccountTypeEnum;
    operator: string;
    operation: InvoiceOperationEnum;
    note?: string;
    timestamp: string;
  }

  export interface Response {
    id: string;
    status: AgencyInvoiceStatusEnum;
    agentId: string;
    agentName: string;
    targetMonth: IDate;
    destination?: Destination;
    reports: AgencySalesReports;
    discrepancies?: Discrepancy[];
    invoiceDocumentUrl?: string;
    bankTransferDetail?: BankTransferDetail;
    operationLogs: OperationLog[];
    availableOperations: InvoiceOperationEnum[];
    accountingSlipFinshed?: boolean;
    generatedAt: string;
    currency: string;
    totalPrice: number;
    version: string;
  }
}

export namespace update_agency_invoice {
  export interface InitiallyConfirm {
    discrepancies?: InitiallyConfirm.Discrepancy[];
  }

  export namespace InitiallyConfirm {
    export interface Discrepancy {
      title: string;
      currency: string;
      amount: number;
    }
  }

  export interface SupervisorConfirm {
    reject?: boolean;
  }

  export interface AccountingConfirm {
    reject?: boolean;
  }

  export interface ConfirmPayment {
    actualAmount: number;
    date: IDate;
    bankNumber: string;
    bankName: string;
    branchNumber: string;
    branchName: string;
    accountNumber: string;
    accountName: string;
    businessCode: string;
    accountCode: string;
    auxiliaryCode: string;
  }

  export interface AnnotateAccountingSlipFinshed {
    revoke: boolean;
  }

  export interface Request {
    invoiceId: string;
    operatorName: string;
    initiallyConfirm?: InitiallyConfirm;
    supervisorConfirm?: SupervisorConfirm;
    accountingConfirm?: AccountingConfirm;
    annotateAccountingSlipFinshed?: AnnotateAccountingSlipFinshed;
    confirmPayment?: ConfirmPayment;
    note?: string;
    version: string;
  }

  export interface Response {}
}

// mock
const useMock = process.env.VUE_APP_API_USE_MOCK === 'true';

export const mockInvoiceSummariesCache: Map<
  string,
  search_invoices.InvoiceSummary
> = new Map();

export const mockAgencyInvoiceDetailsCache: Map<
  string,
  get_agency_invoice_detail.Response
> = new Map();

export const mockInternalNewInvoiceFromBooking = (
  bookingDetail: get_booking_detail.Response
) => {
  const agent = mockAgentSummariesCache.get(bookingDetail.agentId)!;
  const now = new Date();
  const invoiceSummary: search_invoices.InvoiceSummary = {
    id: bookingDetail.bookingId,
    agentId: agent.id,
    agentName: agent.name,
    agentType: agent.type,
    invoiceType: search_invoices.InvoiceTypeEnum.BOOKING_INVOICE,
    status: search_invoices.InvoiceStatusEnum.CONFIRMED,
    generatedAt: now.toLocaleString(),
    billingAmount: bookingDetail.totalPrice,
    paidAmount: bookingDetail.bankTransferDetail
      ? bookingDetail.bankTransferDetail.actualAmount
      : 0,
    paidAt: bookingDetail.bankTransferDetail
      ? bookingDetail.bankTransferDetail.date
      : null,
    orders: [],
    isDelayed: false,
    accountingSlipFinished: bookingDetail.accountingSlipFinshed,
    notes: bookingDetail.operationLogs
      .filter(
        l =>
          l.note &&
          ((l.operation as OperationEnum) === OperationEnum.NOTIFY_PAYMENT ||
            (l.operation as OperationEnum) === OperationEnum.CONFIRM_PAYMENT)
      )
      .map(l => l.note!),
    extraInfo: {},
  };
  const ordersByProductId: Map<string, search_invoices.OrderInfo> = new Map();
  bookingDetail.orders.forEach(o => {
    o.products.forEach(p => {
      if (!ordersByProductId.has(p.id)) {
        ordersByProductId.set(p.id, {
          productId: p.id,
          productName: p.name,
          soldAmount: 0,
          refundAmount: 0,
          type: p.type,
        });
      }
      const order = ordersByProductId.get(p.id)!;
      order.soldAmount += p.amount;
    });
  });
  invoiceSummary.orders = Array.from(ordersByProductId.values());
  mockInvoiceSummariesCache.set(invoiceSummary.id, invoiceSummary);
};

export const mockInternalUpdateInvoiceStatusFromBooking = (
  bookingDetail: get_booking_detail.Response & WithInternalStatus
) => {
  mockInvoiceSummariesCache.get(
    bookingDetail.bookingId
  )!.status = bookingStatusToInvoiceStatus(bookingDetail.internalStatus);
};

export const mockInternalDeleteInvoiceFromBooking = (
  bookingDetail: get_booking_detail.Response
) => {
  mockInvoiceSummariesCache.delete(bookingDetail.bookingId);
};

export const mockInternalNewInvoiceFromAgencySalesReports = (
  agencySalesReportDetails: get_agency_sales_report_detail.Response[],
  generatedAt: Date
) => {
  const agent = mockAgentSummariesCache.get(
    agencySalesReportDetails[0].agentId
  )!;
  const agentDetail = mockAgentsCache.get(agent.id)!;
  const destination: get_agency_invoice_detail.Destination = {
    address: JSON.stringify(agentDetail.shippingAddress),
    recipient:
      agentDetail.shippingReceivers && agentDetail.shippingReceivers.length > 0
        ? agentDetail.shippingReceivers[0].name
        : '',
    contact:
      agentDetail.shippingReceivers && agentDetail.shippingReceivers.length > 0
        ? `Phone: ${agentDetail.shippingReceivers[0].phoneNumber}, Fax: ${agentDetail.shippingReceivers[0].faxNumber}, Email: ${agentDetail.shippingReceivers[0].emailAddress}`
        : '',
  };

  const id = randomString(4);
  const agencyInvoiceDetail: get_agency_invoice_detail.Response = {
    id: id,
    agentId: agent.id,
    agentName: agent.name,
    status: AgencyInvoiceStatusEnum.INVOICE_GENERATED,
    targetMonth: agencySalesReportDetails[0].targetMonth,
    destination: destination,
    reports: {
      reports: [],
      grandTotalGrossPrice: 0,
      grandTotalNetPrice: 0,
    },
    operationLogs: [],
    availableOperations: [InvoiceOperationEnum.INVOICE_INITIALLY_CONFIRM],
    generatedAt: generatedAt.toLocaleString(),
    currency: '',
    totalPrice: 0,
    version: `${generatedAt.getTime()}`,
  };
  agencySalesReportDetails.forEach(r => {
    const report: get_agency_invoice_detail.AgencySalesReport = {
      id: r.id,
      products: [],
      currency: r.products[0].settlement.currency,
      totalNetPrice: 0,
      totalGrossPrice: 0,
      reportedAt: r.signedAt,
    };
    r.products.forEach(p => {
      const price = mockProductSummariesCache.get(p.productId)!.price;
      const product: get_agency_invoice_detail.Product = {
        productId: p.productId,
        productName: p.productName,
        amount: p.inventory.arrivalAmount,
        currency: p.settlement.currency,
        totalNetPrice: p.inventory.arrivalAmount * (price ? price.netPrice : 0),
        totalGrossPrice:
          p.inventory.arrivalAmount * (price ? price.listPrice : 0),
      };
      report.products.push(product);
      report.totalGrossPrice += product.totalGrossPrice;
      report.totalNetPrice += product.totalNetPrice;
    });
    agencyInvoiceDetail.reports.reports.push(report);
    agencyInvoiceDetail.reports.grandTotalGrossPrice += report.totalGrossPrice;
    agencyInvoiceDetail.reports.grandTotalNetPrice += report.totalNetPrice;
  });
  agencyInvoiceDetail.currency =
    agencyInvoiceDetail.reports.reports[0].currency;
  agencyInvoiceDetail.totalPrice =
    agencyInvoiceDetail.reports.grandTotalNetPrice;
  mockAgencyInvoiceDetailsCache.set(
    agencyInvoiceDetail.id,
    agencyInvoiceDetail
  );

  const invoiceSummary: search_invoices.InvoiceSummary = {
    id: agencyInvoiceDetail.id,
    agentId: agent.id,
    agentName: agent.name,
    agentType: agent.type,
    invoiceType: search_invoices.InvoiceTypeEnum.AGENTCY_INVOICE,
    status: agencyInvoiceStatusToInvoiceStatus(agencyInvoiceDetail.status),
    generatedAt: agencyInvoiceDetail.generatedAt,
    billingAmount: agencyInvoiceDetail.totalPrice,
    paidAmount: agencyInvoiceDetail.bankTransferDetail
      ? agencyInvoiceDetail.bankTransferDetail.actualAmount
      : 0,
    paidAt: agencyInvoiceDetail.bankTransferDetail
      ? agencyInvoiceDetail.bankTransferDetail.date
      : null,
    orders: [],
    isDelayed: false,
    accountingSlipFinished: agencyInvoiceDetail.accountingSlipFinshed,
    notes: agencyInvoiceDetail.operationLogs
      .filter(
        l =>
          l.note &&
          (l.operation as InvoiceOperationEnum) ===
            InvoiceOperationEnum.INVOICE_CONFIRM_PAYMENT
      )
      .map(l => l.note!),
    extraInfo: {},
  };
  const ordersByProductId: Map<string, search_invoices.OrderInfo> = new Map();
  agencySalesReportDetails.forEach(r => {
    r.products.forEach(p => {
      if (!ordersByProductId.has(p.productId)) {
        ordersByProductId.set(p.productId, {
          productId: p.productId,
          productName: p.productName,
          soldAmount: 0,
          refundAmount: 0,
          type: mockProductSummariesCache.get(p.productId)!.type,
        });
      }
      const order = ordersByProductId.get(p.productId)!;
      order.soldAmount += p.inventory.soldAmount;
      order.refundAmount += p.inventory.refundedAmount;
    });
  });
  invoiceSummary.orders = Array.from(ordersByProductId.values());
  mockInvoiceSummariesCache.set(invoiceSummary.id, invoiceSummary);
};

const mockSearchInvoices = (req: search_invoices.Request) => {
  const invoices: search_invoices.InvoiceSummary[] = [];
  mockInvoiceSummariesCache.forEach(i => {
    if (req.agentIds.some(agentId => agentId === i.agentId)) {
      let match = true;

      if (
        req.invoiceTypes &&
        req.invoiceTypes.length > 0 &&
        req.invoiceTypes.every(
          t =>
            (t as search_invoices.InvoiceTypeEnum) !==
            (i.invoiceType as search_invoices.InvoiceTypeEnum)
        )
      ) {
        match = false;
      }

      if (
        (req.generatedDateRange &&
          req.generatedDateRange!.start &&
          new Date(stringFromIDate(req.generatedDateRange!.start)!) >
            new Date(i.generatedAt)) ||
        (req.generatedDateRange!.end &&
          new Date(stringFromIDate(req.generatedDateRange!.end)!) <
            new Date(i.generatedAt))
      ) {
        match = false;
      }

      if (
        req.targetYear !== void 0 &&
        (i.invoiceType as search_invoices.InvoiceTypeEnum) ===
          search_invoices.InvoiceTypeEnum.AGENTCY_INVOICE &&
        req.targetYear !==
          mockAgencyInvoiceDetailsCache.get(i.id)!.targetMonth.year
      ) {
        match = false;
      }

      if (req.priceRange) {
        let totalPrice = 0;
        if (
          (i.invoiceType as search_invoices.InvoiceTypeEnum) ===
          search_invoices.InvoiceTypeEnum.AGENTCY_INVOICE
        ) {
          totalPrice = mockAgencyInvoiceDetailsCache.get(i.id)!.reports
            .grandTotalNetPrice;
        } else {
          totalPrice = mockBookingDetailsCache.get(i.id)!.totalPrice;
        }
        if (
          req.priceRange.from !== void 0 &&
          totalPrice < req.priceRange.from
        ) {
          match = false;
        }
        if (req.priceRange.to !== void 0 && totalPrice > req.priceRange.to) {
          match = false;
        }
      }

      if (
        req.targetProductIds &&
        req.targetProductIds.length > 0 &&
        req.targetProductIds.every(pid =>
          i.orders.every(o => o.productId !== pid)
        )
      ) {
        match = false;
      }

      if (
        req.paymentStatuses &&
        req.paymentStatuses.length > 0 &&
        !req.paymentStatuses.some(s => {
          switch (s as search_invoices.PaymentStatusEnum) {
            case search_invoices.PaymentStatusEnum.PAID:
              return !!i.paidAt;
            case search_invoices.PaymentStatusEnum.NOT_PAID:
              return !i.paidAt;
            default:
              return true;
          }
        })
      ) {
        match = false;
      }

      if (
        (req.paidDateRange &&
          req.paidDateRange!.start &&
          stringFromIDate(req.paidDateRange!.start)! >
            stringFromIDate(i.paidAt)!) ||
        (req.paidDateRange!.end &&
          stringFromIDate(req.paidDateRange!.end)! < stringFromIDate(i.paidAt)!)
      ) {
        match = false;
      }

      if (
        req.accountingSlipStatuses &&
        req.accountingSlipStatuses.length > 0 &&
        !req.accountingSlipStatuses.some(s => {
          switch (s as search_invoices.AccountingSlipStatusEnum) {
            case search_invoices.AccountingSlipStatusEnum.FINISHED:
              return !!i.accountingSlipFinished;
            case search_invoices.AccountingSlipStatusEnum.NOT_FINISHED:
              return !i.accountingSlipFinished;
            default:
              return true;
          }
        })
      ) {
        match = false;
      }

      if (
        req.statuses &&
        req.statuses.length > 0 &&
        req.statuses.every(
          s =>
            (s as search_invoices.InvoiceStatusEnum) !==
            (i.status as search_invoices.InvoiceStatusEnum)
        )
      ) {
        match = false;
      }

      if (match) {
        invoices.push(i);
      }
    }
  });
  return {
    invoices: invoices,
    csvBlob: '',
  } as search_invoices.Response;
};

const mockGetAgencyInvoiceDetail = (req: get_agency_invoice_detail.Request) => {
  return JSON.parse(
    JSON.stringify(mockAgencyInvoiceDetailsCache.get(req.invoiceId))
  ) as get_agency_invoice_detail.Response;
};

const updateAgencyInvoiceForInitiallyConfirm = (
  detail: get_agency_invoice_detail.Response,
  req: update_agency_invoice.Request
) => {
  const now = new Date();
  if (
    req.initiallyConfirm &&
    req.initiallyConfirm.discrepancies &&
    req.initiallyConfirm.discrepancies.length > 0
  ) {
    detail.discrepancies = req.initiallyConfirm.discrepancies;
    detail.discrepancies.forEach(d => {
      detail.totalPrice += d.amount;
    });
  }
  detail.operationLogs.push({
    accountType: AccountTypeEnum.OCTB,
    operator: req.operatorName,
    operation: InvoiceOperationEnum.INVOICE_INITIALLY_CONFIRM,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.availableOperations = [
    InvoiceOperationEnum.INVOICE_SUPERVISOR_CONFIRM,
    InvoiceOperationEnum.INVOICE_SUPERVISOR_CONFIRM_REJECT,
  ];
  detail.status = AgencyInvoiceStatusEnum.INVOICE_CONFIRMED_INIT;
  detail.version = `${now.getTime()}`;
};

const updateAgencyInvoiceForSupervisorConfirm = (
  detail: get_agency_invoice_detail.Response,
  req: update_agency_invoice.Request
) => {
  const now = new Date();
  if (req.supervisorConfirm && req.supervisorConfirm.reject) {
    (detail.discrepancies || []).forEach(d => (detail.totalPrice -= d.amount));
    delete detail.discrepancies;

    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: InvoiceOperationEnum.INVOICE_SUPERVISOR_CONFIRM_REJECT,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
    detail.availableOperations = [
      InvoiceOperationEnum.INVOICE_INITIALLY_CONFIRM,
    ];
    detail.status = AgencyInvoiceStatusEnum.INVOICE_GENERATED;
  } else {
    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: InvoiceOperationEnum.INVOICE_SUPERVISOR_CONFIRM,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
    detail.availableOperations = [
      InvoiceOperationEnum.INVOICE_ACCOUNTING_CONFIRM,
      InvoiceOperationEnum.INVOICE_ACCOUNTING_CONFIRM_REJECT,
    ];
    detail.status = AgencyInvoiceStatusEnum.INVOICE_CONFIRMED_SUP;
  }
  detail.version = `${now.getTime()}`;
};

const updateAgencyInvoiceForAccountingConfirm = (
  detail: get_agency_invoice_detail.Response,
  req: update_agency_invoice.Request
) => {
  const now = new Date();
  if (req.accountingConfirm && req.accountingConfirm.reject) {
    (detail.discrepancies || []).forEach(d => (detail.totalPrice -= d.amount));
    delete detail.discrepancies;

    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: InvoiceOperationEnum.INVOICE_ACCOUNTING_CONFIRM_REJECT,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
    detail.availableOperations = [
      InvoiceOperationEnum.INVOICE_INITIALLY_CONFIRM,
    ];
    detail.status = AgencyInvoiceStatusEnum.INVOICE_GENERATED;
  } else {
    if (!detail.invoiceDocumentUrl) {
      detail.invoiceDocumentUrl = 'dummy.invoice.url';
    }
    // if (!detail.invoiceDocuments || detail.invoiceDocuments.length === 0) {
    //   const invoiceDocumentsByProductId: Map<
    //     string,
    //     get_agency_invoice_detail.InvoiceDocument
    //   > = new Map();
    //   detail.reports.reports.forEach(r => {
    //     r.products.forEach(p => {
    //       invoiceDocumentsByProductId.has(p.productId) ||
    //         invoiceDocumentsByProductId.set(p.productId, {
    //           productId: p.productId,
    //           productName: p.productName,
    //           url: `dummy.invoice.url/${p.productId}`,
    //         });
    //     });
    //   });
    //   detail.invoiceDocuments = Array.from(
    //     invoiceDocumentsByProductId.values()
    //   );
    // }
    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: InvoiceOperationEnum.INVOICE_ACCOUNTING_CONFIRM,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
    detail.availableOperations = [InvoiceOperationEnum.INVOICE_CONFIRM_PAYMENT];
    detail.status = AgencyInvoiceStatusEnum.INVOICE_CONFIRMED;
  }
  detail.version = `${now.getTime()}`;
};

const updateAgencyInvoiceForConfirmPayment = (
  detail: get_agency_invoice_detail.Response,
  req: update_agency_invoice.Request
) => {
  const now = new Date();
  detail.bankTransferDetail = {
    actualAmount: req.confirmPayment!.actualAmount,
    date: req.confirmPayment!.date,
    bankNumber: req.confirmPayment!.bankNumber,
    bankName: req.confirmPayment!.bankName,
    branchNumber: req.confirmPayment!.branchNumber,
    branchName: req.confirmPayment!.branchName,
    accountNumber: req.confirmPayment!.accountNumber,
    accountName: req.confirmPayment!.accountName,
    businessCode: req.confirmPayment!.businessCode,
    accountCode: req.confirmPayment!.accountCode,
    auxiliaryCode: req.confirmPayment!.auxiliaryCode,
  };
  detail.operationLogs.push({
    accountType: AccountTypeEnum.OCTB,
    operator: req.operatorName,
    operation: InvoiceOperationEnum.INVOICE_CONFIRM_PAYMENT,
    timestamp: `${now.toLocaleString()}`,
    note: req.note,
  });
  detail.availableOperations = [
    InvoiceOperationEnum.INVOICE_ANNOTATE_ACCOUNTING_SLIP_FINSHED,
  ];
  detail.status = AgencyInvoiceStatusEnum.INVOICE_PAYMENT_CONFIRMED;
  detail.version = `${now.getTime()}`;
};

const updateAgencyInvoiceForAnnotateAccountingSlipFinshed = (
  detail: get_agency_invoice_detail.Response,
  req: update_agency_invoice.Request
) => {
  const now = new Date();
  if (
    req.annotateAccountingSlipFinshed &&
    req.annotateAccountingSlipFinshed!.revoke
  ) {
    detail.accountingSlipFinshed = false;
    detail.availableOperations = detail.availableOperations
      .filter(
        o =>
          (o as InvoiceOperationEnum) !==
          InvoiceOperationEnum.INVOICE_ANNOTATE_ACCOUNTING_SLIP_FINSHED
      )
      .concat(
        InvoiceOperationEnum.INVOICE_ANNOTATE_ACCOUNTING_SLIP_FINSHED_REVOKE
      );
    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation:
        InvoiceOperationEnum.INVOICE_ANNOTATE_ACCOUNTING_SLIP_FINSHED_REVOKE,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
  } else {
    detail.accountingSlipFinshed = true;
    detail.availableOperations = detail.availableOperations
      .filter(
        o =>
          (o as InvoiceOperationEnum) !==
          InvoiceOperationEnum.INVOICE_ANNOTATE_ACCOUNTING_SLIP_FINSHED_REVOKE
      )
      .concat(InvoiceOperationEnum.INVOICE_ANNOTATE_ACCOUNTING_SLIP_FINSHED);
    detail.operationLogs.push({
      accountType: AccountTypeEnum.OCTB,
      operator: req.operatorName,
      operation: InvoiceOperationEnum.INVOICE_ANNOTATE_ACCOUNTING_SLIP_FINSHED,
      timestamp: `${now.toLocaleString()}`,
      note: req.note,
    });
  }
  detail.version = `${now.getTime()}`;
};

const mockUpdateAgencyInvoice = (req: update_agency_invoice.Request) => {
  const detail = mockAgencyInvoiceDetailsCache.get(req.invoiceId);
  if (detail) {
    if (detail.version !== req.version) {
      throw new Error(
        `mock version not matched, req: ${req.version}, actual: ${detail.version}`
      );
    }
    if (
      (detail.status as AgencyInvoiceStatusEnum) ===
        AgencyInvoiceStatusEnum.INVOICE_GENERATED &&
      req.initiallyConfirm
    ) {
      updateAgencyInvoiceForInitiallyConfirm(detail, req);
    } else if (
      (detail.status as AgencyInvoiceStatusEnum) ===
        AgencyInvoiceStatusEnum.INVOICE_CONFIRMED_INIT &&
      req.supervisorConfirm
    ) {
      updateAgencyInvoiceForSupervisorConfirm(detail, req);
    } else if (
      (detail.status as AgencyInvoiceStatusEnum) ===
        AgencyInvoiceStatusEnum.INVOICE_CONFIRMED_SUP &&
      req.accountingConfirm
    ) {
      updateAgencyInvoiceForAccountingConfirm(detail, req);
    } else if (
      (detail.status as AgencyInvoiceStatusEnum) ===
        AgencyInvoiceStatusEnum.INVOICE_CONFIRMED &&
      req.confirmPayment
    ) {
      updateAgencyInvoiceForConfirmPayment(detail, req);
    } else if (req.annotateAccountingSlipFinshed) {
      updateAgencyInvoiceForAnnotateAccountingSlipFinshed(detail, req);
    } else {
      throw new Error(
        `unknown status: ${detail.status}, req: ${JSON.stringify(req)}`
      );
    }
    mockInvoiceSummariesCache.get(
      detail.id
    )!.status = agencyInvoiceStatusToInvoiceStatus(detail.status);
  } else {
    throw new Error('mock agency invoice not found');
  }
  return {} as update_agency_invoice.Response;
};

// api
export const searchInvoices = async (req: search_invoices.Request) => {
  if (useMock) {
    await sleep(300);
    return mockSearchInvoices(req);
  }

  const res = await request({
    url: '/v1/search-invoices',
    method: 'post',
    data: req,
  });
  return res.data as search_invoices.Response;
};

export const getAgencyInvoiceDetail = async (
  req: get_agency_invoice_detail.Request
) => {
  if (useMock) {
    await sleep(300);
    return mockGetAgencyInvoiceDetail(req);
  }

  const res = await request({
    url: '/v1/get-agency-invoice-detail',
    method: 'post',
    data: req,
  });
  return res.data as get_agency_invoice_detail.Response;
};

export const updateAgencyInvoice = async (
  req: update_agency_invoice.Request
) => {
  if (useMock) {
    await sleep(300);
    return mockUpdateAgencyInvoice(req);
  }

  const res = await request({
    url: '/v1/update-agency-invoice',
    method: 'post',
    data: req,
  });
  return res.data as update_agency_invoice.Response;
};
